import { useState, type ReactNode } from 'react';
import type { GameState, SeatId, TerritoryId } from '@risk/engine';
import { TerritoryList } from './TerritoryList.js';
import { MapView } from '../map/MapView.js';

/**
 * Map or list, with a toggle.
 *
 * Neither is the fallback for the other. For placing armies and for the
 * end-of-turn audit the list is genuinely the better tool — it is a column of
 * numbers to check against a physical board, and accuracy beats prettiness.
 * For attacking, the map is, because the question there is spatial. So each
 * phase asks for what suits it, and an explicit choice by the player overrides
 * that for the rest of the session.
 */

export type BoardMode = 'map' | 'list';

const KEY = 'risk.board';

function storedChoice(): BoardMode | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'map' || v === 'list' ? v : null;
  } catch {
    return null;
  }
}

export function BoardView({
  game,
  seatId,
  prefer,
  selected,
  targets,
  onSelect,
  filter,
  trailing,
}: {
  game: GameState;
  seatId: SeatId | null;
  /** What this phase would like, used until the player says otherwise. */
  prefer: BoardMode;
  selected?: TerritoryId | null | undefined;
  targets?: ReadonlySet<TerritoryId> | undefined;
  onSelect?: ((id: TerritoryId) => void) | undefined;
  filter?: ((id: TerritoryId) => boolean) | undefined;
  trailing?: ((id: TerritoryId) => ReactNode) | undefined;
}): ReactNode {
  const [chosen, setChosen] = useState<BoardMode | null>(storedChoice);
  const mode: BoardMode = chosen ?? prefer;

  const choose = (next: BoardMode): void => {
    setChosen(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // The choice still holds for this session.
    }
  };

  return (
    <div>
      <div className="viewtoggle" role="group" aria-label="Board view">
        <button aria-pressed={mode === 'map'} onClick={() => choose('map')}>Map</button>
        <button aria-pressed={mode === 'list'} onClick={() => choose('list')}>List</button>
      </div>

      {mode === 'map' ? (
        <MapView
          game={game}
          seatId={seatId}
          selected={selected}
          targets={targets}
          onSelect={onSelect}
        />
      ) : (
        <TerritoryList
          game={game}
          seatId={seatId}
          selected={selected}
          targets={targets}
          onSelect={onSelect}
          filter={filter}
          trailing={trailing}
        />
      )}
    </div>
  );
}
